import { memo, useState } from "react"
import { brand } from "@/assets/brand"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { parseSelectionMessage } from "@/features/oio/lib/selectionPrompt"
import { isClarifyingQuestion } from "../lib/clarifyingQuestion"
import { MessageContent } from "./MessageContent"

export interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  createdAt?: Date
}

interface MessageBubbleProps {
  message: ChatMessage
}

// Content longer than this collapses behind a "Show more" toggle. Picked to
// comfortably clear a couple of short paragraphs (so ordinary replies never
// clamp) while catching pasted multi-clause confirmation text, which is the
// case that was rendering as an unreadable wall of text.
const COLLAPSE_THRESHOLD = 600

function initials(name: string | null | undefined, email?: string): string {
  const src = (name ?? "").trim() || (email ?? "").split("@")[0] || ""
  const parts = src.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return "You"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export const MessageBubble = memo(function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === "user"
  const { session } = useAuth()
  const avatar = isUser ? initials(session?.user?.displayName, session?.user?.email) : "N"
  const isQuestion = !isUser && isClarifyingQuestion(message.content)
  // A message sent from the draft's selection toolbar carries its quoted
  // passage inline; render the quote as a compact block and only the actual
  // question as the message body.
  const selection = isUser ? parseSelectionMessage(message.content) : null
  const bodyContent = selection ? selection.question : message.content
  const isLong = bodyContent.length > COLLAPSE_THRESHOLD
  // Per-message state: each bubble tracks its own expanded/collapsed state,
  // never shared across messages.
  const [expanded, setExpanded] = useState(false)
  const collapsed = isLong && !expanded

  return (
    <div className={"msg" + (isQuestion ? " msg--question" : "")}>
      <div className={"msg__av " + (isUser ? "msg__av--me" : "msg__av--ai")}>
        {isUser ? avatar : <img className="msg__av-logo" src={brand.emblem} alt="NitiAssist AI" />}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="msg__name">
          {isUser ? "You" : "NitiAssist AI"}
          {isQuestion && <span className="msg__question-tag">Needs your reply</span>}
        </div>
        <div className="msg__body">
          {selection && (
            <div className="msg__quote" data-testid="msg-selection-quote">
              <span className="msg__quote-label">Selected from the draft</span>
              <p>{selection.quote}</p>
            </div>
          )}
          <div className={collapsed ? "msg__body-clamp" : undefined}>
            <MessageContent content={bodyContent} role={message.role} />
          </div>
          {isLong && (
            <button
              type="button"
              className="msg__more-btn"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      </div>
    </div>
  )
})
